import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatPrice, getImageUrl, getAmountFromPriceObject } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { useCategoryTranslation } from '@/hooks/use-category-translation';
import type { Product } from '@/lib/types';

import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { StatusBadge } from '@/components/molecules/status-badge';

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const { t } = useTranslation();
  const { currency } = useConfigStore();
  const { translateCategory } = useCategoryTranslation();
  const mainImage = getImageUrl(product.images?.[0]);

  return (
    <Link to={`/products/${product.id}`}>
      <Card className="overflow-hidden hover:border-primary/50 transition-colors cursor-pointer">
        <div className="aspect-square relative bg-muted">
          {mainImage ? (
            <img
              src={mainImage}
              alt={product.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              No image
            </div>
          )}
          <div className="absolute top-2 right-2">
            <StatusBadge status={product.status} />
          </div>
        </div>
        <CardContent className="p-4">
          <h3 className="font-medium line-clamp-2">{product.name}</h3>
          <div className="mt-2 flex items-center justify-between">
            <div>
              <p className="font-bold">{formatPrice(getAmountFromPriceObject(product.prices, currency, product.price), currency)}</p>
              {product.comparePrice && product.comparePrice > product.price && (
                <p className="text-sm text-muted-foreground line-through">
                  {formatPrice(product.comparePrice, currency)}
                </p>
              )}
            </div>
            <Badge variant="secondary">{product.stock} {t('products.inStock')}</Badge>
          </div>
          {product.categoryName && (
            <p className="mt-2 text-sm text-muted-foreground">
              {translateCategory(product.categoryName)}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
